import { RuntimeMediaQuery } from '@duda-co/responsive-styles/RuntimeMediaQuery';
import type { ResponsiveStylesStructured } from '@duda-co/responsive-styles/types/ResponsiveStylesTypes';
import { Div } from 'client/widget-components/basicComponents';
import FileUpload from 'client/widget-components/components/FileUpload';
import { FileStackSource } from 'client/widget-components/components/FileUpload/consts';
import type {
    FileUploadState,
    UploadResult,
} from 'client/widget-components/components/FileUpload/types';
import { Customization } from 'client/widget-components/ecom/stores/types';
import { mergeResponsiveStyles } from 'client/widget-components/responsive-styles/responsiveStylesService';
import React, { FC, memo, useCallback } from 'react';
import { CUSTOMIZATIONS_DATA_GRABS } from '../../constants';
import type {
    PCFileStackConfig,
    ProductCustomizationsStyles,
} from '../../types';
import BaseProductCustomizationLabel from '../BaseProductCustomizationLabel';
import PaidAddonLabel from '../PaidAddonLabel';
import CustomizationFieldError from '../CustomizationFieldError';

interface FileCustomizationProps extends Customization {
    styles?: ProductCustomizationsStyles;
    error?: string | null;
    onChange: (fieldId: string, value: UploadResult[]) => void;
    validateField: (fieldId: string) => void;
    filestackConfig: PCFileStackConfig;
}

const ALLOWED_FILE_TYPES = [
    'image/*',
    'application/pdf',
    // doc
    'application/msword',
    // docx
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

const FileCustomization: FC<FileCustomizationProps> = ({
    id,
    label,
    mandatory = false,
    price,
    displayed_price,
    error,
    styles,
    onChange,
    validateField,
    button_label,
    filestackConfig,
}) => {
    const handleFileChange = useCallback(
        (state: FileUploadState) => {
            onChange(id, state.files);
            validateField(id);
        },
        [onChange, validateField, id]
    );

    const uploadButton = styles?.uploadButton;

    const fileUploadStyles = {
        button: {
            ...uploadButton,
            root: mergeResponsiveStyles([
                buttonDefaultStyles,
                ...(!button_label ? [buttonNoLabelStyles] : []),
                uploadButton?.root,
            ]),
            rootHover: uploadButton?.rootHover,
            rootDisabled: uploadButton?.rootDisabled,
            text: mergeResponsiveStyles([
                textDefaultStyles,
                uploadButton?.text,
            ]),
            rootHoverText: uploadButton?.rootHoverText,
            disabledText: uploadButton?.disabledText,
            icon: mergeResponsiveStyles([
                iconDefaultStyles,
                uploadButton?.icon,
            ]),
            rootHoverIcon: uploadButton?.rootHoverIcon,
            iconDisabled: uploadButton?.iconDisabled,
        },
    };

    return (
        <Div
            data-auto='product-customizations-file'
            data-grab={CUSTOMIZATIONS_DATA_GRABS.fileWrapperDataGrab}
            styles={styles?.fileWrapper}
        >
            <BaseProductCustomizationLabel
                label={
                    <PaidAddonLabel
                        label={label}
                        price={price}
                        displayedPrice={displayed_price}
                        styles={styles?.filePaidAddonText}
                    />
                }
                required={mandatory}
                styles={mergeResponsiveStyles([
                    labelDefaultStyles,
                    styles?.label,
                ])}
                dataGrab={CUSTOMIZATIONS_DATA_GRABS.labelDataGrab}
                dataAuto='product-customizations-file-label'
            />
            <FileUpload
                buttonText={button_label}
                onChange={handleFileChange}
                styles={fileUploadStyles}
                dataGrab={CUSTOMIZATIONS_DATA_GRABS.fileUploadDataGrab}
                dataAuto={`product-customizations-file-upload-${id}`}
                buttonClassName='button_2'
                apiKey={filestackConfig.apiKey}
                pickerOptions={{
                    accept: ALLOWED_FILE_TYPES,
                    fromSources: [FileStackSource.LOCAL_FILE_SYSTEM],
                    maxSize: 1024 * 1024 * 10, // 10MB
                    disableAltText: true,
                    storeTo: {
                        location: 'S3',
                        access: 'public',
                        container: filestackConfig.storeContainer,
                        path: filestackConfig.storePath,
                        region: filestackConfig.storeRegion,
                    },
                }}
            />

            {!!error && <CustomizationFieldError error={error} />}
        </Div>
    );
};

const labelDefaultStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        marginBlockEnd: '8px',
    },
};

const buttonDefaultStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        width: 'min-content',
        height: '40px',
        borderRadius: '4px',
        borderWidth: '1px',
        paddingTop: '8px',
        padding: '8px 18px',
        backgroundColor: 'var(--btn-bg-color)',
        borderColor: 'var(--color_1);',
        margin: '0px',
        gap: '8px',
        direction: 'ltr',
        backgroundImage: 'none',
        boxShadow: 'none',
        textAlign: 'center',
    },
};

const buttonNoLabelStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        gap: 0,
    },
};

const textDefaultStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        color: 'var(--btn-text-color)',
        padding: '0px',
        fontSize: '14px',
        fontWeight: 'normal',
        textDecoration: 'none',
        fontStyle: 'normal',
    },
};

const iconDefaultStyles: ResponsiveStylesStructured = {
    [RuntimeMediaQuery.COMMON]: {
        color: 'var(--btn-icon-color)',
        display: 'flex',
        width: '16px',
        height: '16px',
    },
};

export default memo(FileCustomization);
